import * as React from 'react'

import type { RecentInteractionRow } from '@sdr-agend/admin-schemas'
import { Badge } from '@/components/ui/badge'
import { formatRelativeTime } from '@/lib/format'

/**
 * "Últimas Interações da IA" (Story 10.7, 2026-07-30, user request, AC5) — as
 * conversas mais recentes, como uma lista de atividade. Deliberadamente SEM
 * o texto da última mensagem do cliente — `status`/`lastIntent` comunicam "o
 * que está acontecendo" sem expor conteúdo (ver Dev Notes/Riscos da Story
 * 10.7); mesma convenção de badge/tone de `ConversationDetail.tsx`'s
 * `STATUS_TONE`, com o status exibido no idioma original do enum (sem
 * tradução própria pra não divergir do resto do admin).
 */
export interface RecentInteractionsListProps {
  data: ReadonlyArray<RecentInteractionRow>
}

const STATUS_TONE: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'success' | 'outline'
> = {
  scheduled: 'secondary',
  awaiting_response: 'secondary',
  awaiting_qualification: 'secondary',
  awaiting_slots: 'secondary',
  awaiting_slot_choice: 'secondary',
  awaiting_human_followup: 'secondary',
  active: 'default',
  confirmed: 'success',
  cancelled: 'destructive',
  rescheduled: 'secondary',
  human_handoff: 'destructive',
  cold: 'outline',
  declined: 'destructive',
  attended: 'success',
  no_show: 'destructive',
  closed: 'outline',
  error: 'destructive',
}

export function RecentInteractionsList({ data }: RecentInteractionsListProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="recent-interactions-empty"
      >
        Nenhuma interação recente para os filtros selecionados.
      </div>
    )
  }

  return (
    <ul className="divide-y" data-testid="recent-interactions-list">
      {data.map((row) => (
        <li
          key={row.conversationId}
          className="flex items-center justify-between gap-3 py-2 text-sm"
          data-testid={`recent-interaction-${row.conversationId}`}
        >
          <div className="min-w-0">
            <p className="truncate font-medium">{row.customerName ?? 'Cliente sem nome'}</p>
            <p className="truncate text-xs text-muted-foreground">
              {row.campaignName}
              {row.lastIntent !== null && ` · ${row.lastIntent}`}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <Badge variant={STATUS_TONE[row.status] ?? 'outline'} className="text-[10px]">
              {row.status}
            </Badge>
            <span className="text-[11px] text-muted-foreground">
              {formatRelativeTime(row.lastMessageAt ?? row.createdAt)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  )
}

export default RecentInteractionsList
